// Карточка «Аккаунт» в Настройках (только браузерный режим)
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUserOutlined';
import { logout, sendVerificationEmail } from '../services/authService';
import { unsubscribeOnLogout } from '../services/pushService';
import { extractErrorMessage } from '../utils/errors';

const SURFACE = '#1b1b1d'; // карточка rgba(255,255,255,0.04) поверх фона страницы
const OK = '#66bb6a';
const WARN = '#f0a43a';
const ERROR_COLOR = '#ff6b6b';

const RESEND_COOLDOWN_SECONDS = 60;
const SENT_AT_KEY = 'emailVerifySentAt';
const UNVERIFIED_SEEN_KEY = 'emailUnverifiedSeen';

const pulse = keyframes`
  from { box-shadow: 0 0 0 0 rgba(102,187,106,0.5); }
  to { box-shadow: 0 0 0 8px rgba(102,187,106,0); }
`;

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // без localStorage отсчёт просто не переживёт перезагрузку
  }
}

// Сколько секунд осталось до новой отправки (отсчёт от времени в localStorage)
function getSecondsLeft() {
  const sentAt = Number(readStorage(SENT_AT_KEY));
  if (!sentAt) return 0;
  const elapsed = Math.floor((Date.now() - sentAt) / 1000);
  return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
}

function formatCountdown(secondsLeft) {
  const shown = Math.max(0, secondsLeft - 1); // сразу после нажатия — 0:59
  return `${Math.floor(shown / 60)}:${String(shown % 60).padStart(2, '0')}`;
}

function useResendCooldown() {
  const [secondsLeft, setSecondsLeft] = useState(getSecondsLeft);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => setSecondsLeft(getSecondsLeft()), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    writeStorage(SENT_AT_KEY, String(Date.now()));
    setSecondsLeft(getSecondsLeft());
  };

  return [secondsLeft, start];
}

// Пульс бейджа один раз, если статус сменился с «не подтверждена» на «подтверждена»
// (в том числе после возврата по ссылке из письма: профиль к тому моменту смонтирован заново)
function useVerifiedPulse(email, emailVerified) {
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    if (!email) return;
    const key = `${UNVERIFIED_SEEN_KEY}:${email}`;
    if (emailVerified === false) {
      writeStorage(key, '1');
    } else if (emailVerified === true && readStorage(key)) {
      writeStorage(key, null);
      setPulsing(true);
    }
  }, [email, emailVerified]);

  return pulsing;
}

function StatusBadge({ verified, pulsing }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        right: -2,
        bottom: -2,
        width: 20,
        height: 20,
        boxSizing: 'border-box',
        border: `2px solid ${SURFACE}`,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: verified ? OK : WARN,
        color: verified ? '#0f1a10' : '#1a0f0a',
        fontSize: '11px',
        fontWeight: 800,
        lineHeight: 1,
        ...(pulsing && {
          animation: `${pulse} 600ms ease-out 1`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
      }}
    >
      {verified ? <CheckIcon sx={{ fontSize: 12 }} /> : '!'}
    </Box>
  );
}

function StatusChip({ verified }) {
  const Icon = verified ? VerifiedUserIcon : MailOutlineIcon;
  return (
    <Box
      sx={{
        mt: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        px: '8px',
        py: '2px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: verified ? 'rgba(102,187,106,0.12)' : 'rgba(240,164,58,0.14)',
        color: verified ? '#81c784' : WARN,
      }}
    >
      <Icon sx={{ fontSize: 13 }} />
      {verified ? 'Почта подтверждена' : 'Не подтверждена'}
    </Box>
  );
}

function ResendBlock() {
  const [secondsLeft, startCooldown] = useResendCooldown();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (sending || secondsLeft > 0) return;
    setError('');
    setSending(true);
    startCooldown();
    try {
      await sendVerificationEmail();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        px: '12px',
        py: '10px',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.04)',
        fontSize: '13px',
        color: '#a1a1aa',
      }}
    >
      Мы отправили письмо со ссылкой. Не пришло — проверьте папку «Спам».
      <Box sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleResend}
          disabled={sending || secondsLeft > 0}
          sx={{
            minHeight: 32,
            fontSize: '13px',
            textTransform: 'none',
            color: '#e4e4e7',
            borderColor: 'rgba(255,255,255,0.2)',
            '&:hover': { borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.04)' },
            '&.Mui-disabled': { color: '#71717a', borderColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {secondsLeft > 0 ? `Отправить ещё раз через ${formatCountdown(secondsLeft)}` : 'Отправить ещё раз'}
        </Button>
        {error && (
          <Typography role="alert" sx={{ mt: 0.75, fontSize: '13px', color: ERROR_COLOR }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// createdAt — дата регистрации, если API её отдаёт; иначе подпись слева не показывается
// Сначала отписываем это устройство от push (не дольше 3 с), чтобы следующий пользователь
// не получал чужие уведомления, затем выходим
async function handleLogout() {
  await unsubscribeOnLogout();
  await logout();
}

export default function AccountCard({ email = '', emailVerified, createdAt, sx }) {
  const verified = emailVerified === true;
  const unverified = emailVerified === false;
  const pulsing = useVerifiedPulse(email, emailVerified);

  const createdYear = createdAt ? new Date(createdAt).getFullYear() : null;
  const hasYear = Number.isFinite(createdYear);

  return (
    <Box sx={{ ...sx, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: { md: 40 }, mb: { xs: 1.5, md: 1 } }}>
        <Typography sx={{ fontWeight: 600 }}>Аккаунт</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ position: 'relative', flexShrink: 0, width: 48, height: 48 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 800,
              color: '#fff',
            }}
          >
            {(email.trim()[0] || '?').toUpperCase()}
          </Box>
          {(verified || unverified) && <StatusBadge verified={verified} pulsing={pulsing} />}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={email}
            noWrap
            sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}
          >
            {email || '—'}
          </Typography>
          {(verified || unverified) && <StatusChip verified={verified} />}
        </Box>
      </Box>

      {unverified && <ResendBlock />}

      <Box
        sx={{
          mt: { xs: 2, md: 'auto' },
          pt: 1,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: hasYear ? 'space-between' : 'flex-end',
          gap: 1,
        }}
      >
        {hasYear && (
          <Typography sx={{ fontSize: '13px', color: '#8e8e93' }}>Аккаунт с {createdYear}</Typography>
        )}
        <Button
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            color: ERROR_COLOR,
            textTransform: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(255,107,107,0.08)', boxShadow: 'none' },
          }}
        >
          Выйти
        </Button>
      </Box>
    </Box>
  );
}
